import React, { useMemo, useState, useCallback } from 'react'
import { Box, Link as ChakraLink, Text, IconButton, Tooltip } from '@chakra-ui/react'
import { TableVirtuoso } from 'react-virtuoso'
import { FiExternalLink } from 'react-icons/fi'
import numeral from 'numeral'
import type { ObjectOfBandwidthUrlAndViews } from '../../interfaces'
import AnalyticsFilePreview from './AnalyticsFilePreview'

interface Props {
  data: ObjectOfBandwidthUrlAndViews[] | undefined
  showPreview: boolean
  forceimagePreview: boolean
  scrollEl: HTMLDivElement | null
}

const TableView = ({ data, showPreview, forceimagePreview, scrollEl }: Props): React.ReactElement => {
  const filesList = data ?? []
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null)

  const handleCopy = (url: string) => {
    void navigator.clipboard.writeText(url)
    setCopiedUrl(url)
    setTimeout(() => setCopiedUrl(null), 800)
  }

  const TableComponents = useMemo(() => {
    return {
      Table: ({ style, ...props }: any) => (
        <table
          {...props}
          style={{
            ...style,
            width: '100%',
            borderCollapse: 'collapse',
          }}
        />
      ),
      TableHead: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
        <thead {...props} ref={ref} style={{ position: 'sticky', top: 0, zIndex: 1, background: 'white' }} />
      )),
      TableRow: ({ ...props }) => <tr {...props} style={{ borderBottom: '1px solid #E2E8F0' }} />,
      TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => <tbody {...props} ref={ref} />),
    }
  }, [])

  const fixedHeaderContent = () => {
    return (
      <tr>
        <th
          style={{
            padding: '12px 16px',
            textAlign: 'left',
            fontWeight: 600,
            fontSize: '14px',
            color: '#4A5568',
            width: '120px',
            borderBottom: '2px solid #E2E8F0',
          }}
        >
          Preview
        </th>
        <th
          style={{
            padding: '12px 16px',
            textAlign: 'left',
            fontWeight: 600,
            fontSize: '14px',
            color: '#4A5568',
            borderBottom: '2px solid #E2E8F0',
          }}
        >
          URL
        </th>
        <th
          style={{
            padding: '12px 16px',
            textAlign: 'right',
            fontWeight: 600,
            fontSize: '14px',
            color: '#4A5568',
            width: '70px',
            borderBottom: '2px solid #E2E8F0',
          }}
        >
          Views
        </th>
        <th
          style={{
            padding: '12px 16px',
            textAlign: 'right',
            fontWeight: 600,
            fontSize: '14px',
            color: '#4A5568',
            width: '70px',
            borderBottom: '2px solid #E2E8F0',
          }}
        >
          Bandwidth
        </th>
        <th
          style={{
            padding: '12px 16px',
            textAlign: 'center',
            fontWeight: 600,
            fontSize: '14px',
            color: '#4A5568',
            width: '70px',
            borderBottom: '2px solid #E2E8F0',
          }}
        >
          Action
        </th>
      </tr>
    )
  }

  const rowContent = (_index: number, item: ObjectOfBandwidthUrlAndViews) => {
    return (
      <>
        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
          <Box width="80px" height="80px" borderRadius="4px" overflow="hidden" border="1px solid #E2E8F0">
            <AnalyticsFilePreview url={item.url} thumbnailUrl={item.thumbnailUrl} forceimagePreview={forceimagePreview} />
          </Box>
        </td>
        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
          <Tooltip label={copiedUrl === item.url ? 'Copied' : 'Copy URL'}>
            <Text
              fontSize="sm"
              color="appSlate"
              noOfLines={2}
              wordBreak="break-all"
              textAlign="left"
              cursor="pointer"
              onClick={() => handleCopy(item.url)}
            >
              {item.url}
            </Text>
          </Tooltip>
        </td>
        <td style={{ padding: '12px 16px', textAlign: 'right', verticalAlign: 'middle' }}>
          <Text fontSize="sm" color="appSlate" fontWeight="500">
            {numeral(item.views).format('0,0')}
          </Text>
        </td>
        <td style={{ padding: '12px 16px', textAlign: 'right', verticalAlign: 'middle' }}>
          <Text fontSize="sm" color="appSlate" fontWeight="500">
            {numeral(item.bandwidth).format('0.0ib').replace('i', '')}
          </Text>
        </td>
        <td style={{ padding: '12px 16px', textAlign: 'center', verticalAlign: 'middle' }}>
          <ChakraLink href={item.url} isExternal>
            <IconButton
              aria-label="Open URL"
              icon={<FiExternalLink />}
              size="sm"
              variant="ghost"
              color="appSlate"
              _hover={{ color: 'appBlue' }}
            />
          </ChakraLink>
        </td>
      </>
    )
  }

  if (filesList.length === 0) {
    return <Box padding="24px">No data available</Box>
  }

  if (!scrollEl) {
    return <Box padding="24px">Loading table...</Box>
  }

  return (
    <Box width="100%" >
      <TableVirtuoso
        data={filesList}
        components={TableComponents}
        customScrollParent={scrollEl}
        fixedHeaderContent={fixedHeaderContent}
        itemContent={rowContent}
        style={{ height: '100%' }}
      />
    </Box>
  )
}

export default TableView

